import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { get } from "../api.js";
import type { FamilySetupData } from "../familySetupApi.js";
import { Link, useInRouterContext } from "react-router-dom";
import { AddToCalendarButton } from "../AddToCalendar.js";
import { CalendarFeedSubscription } from "./CalendarFeedSubscription.js";
import { formatDate, formatDay, formatDuration, formatTime } from "../format.js";
import { useOptionalAuth } from "../auth.js";
import { useAsync, type Async } from "../useAsync.js";
import { Pagination, usePagination } from "../Pagination.js";
import { HOME_CREDIT_PREVIEW_PAGE_SIZE } from "../paginationPageSizes.js";
import { FamilyWaitlistOffers, FamilyWaitlistRequests } from "../waitlist/FamilyWaitlist.js";
import {
  booking,
  type BookingOfferings,
  type LessonCreditBlock,
  type LessonCreditSummary,
  type StudentLesson,
  type StudentLessons,
} from "./api.js";
import { creditBookingLink, creditBlockReference, creditDetailsLink, resolveCreditBooking } from "./creditBookingLink.js";
import { BookLesson } from "./BookLesson.js";
import { ChangeLesson, type ChangeMode } from "./ChangeLesson.js";
import { recentAmeliaPurchaseMessage } from "./creditSync.js";
import { describePayment, outcomeLabel, Reasons } from "./policy.js";
import {
  studentBookingStatusLabel,
  studentPaymentBadge,
  studentLessonHasAvailableCredit,
  studentRecurringPlanLabel,
  studentRecurringPlanSummaries,
} from "./studentLessonPresentation.js";

export type StudentDashboardLoadState = "loading" | "error" | "ready";

/** Upcoming rows Home renders: the next lesson, three coming up, and six more. */
export const HOME_UPCOMING_ROWS = 10;
/** Past rows Home renders. */
export const HOME_PAST_ROWS = 10;

export function studentDashboardLoadState({
  lessonsLoading,
  lessonsData,
  lessonsError,
  creditsLoading,
}: {
  lessonsLoading: boolean;
  lessonsData: StudentLessons | null;
  lessonsError: string | null;
  creditsLoading: boolean;
}): StudentDashboardLoadState {
  if (lessonsData) return "ready";
  if (lessonsError) return "error";
  if (lessonsLoading || creditsLoading) return "loading";
  return "error";
}

export function studentDashboardIdentityMatches(
  requestedStudentId: string,
  data: StudentLessons,
): boolean {
  return data.student.id === requestedStudentId;
}

/** Only a vouched, approved booking is firm enough to export as a calendar event. */
export function studentLessonCanExportCalendar(lesson: StudentLesson): boolean {
  const status = lesson.enrichment?.booking_status ?? lesson.status;
  return status === "approved" && !lesson.unavailable;
}

export function studentHasEligibleOnSiteRequest(
  studentId: string,
  offerings: BookingOfferings | null | undefined,
): boolean {
  return offerings?.portal_student_id === studentId &&
    Array.isArray(offerings.offerings) &&
    offerings.offerings.some((offering) => offering.request_onsite.allowed === true);
}

export type StudentBookingReminder =
  | { kind: "one_confirmed_lesson"; lesson: StudentLesson }
  | { kind: "after_completed_lesson"; lesson: StudentLesson };

export type PurchaseClaimReadState = "unknown" | "pending" | "clear";
export type StudentCreditFundingState = "unknown" | "available" | "zero";


export function familyPurchaseClaimReadState(
  data: FamilySetupData | null | undefined,
  loading: boolean,
  error: string | null,
): PurchaseClaimReadState {
  if (loading || error !== null || !data || !Array.isArray(data.claims)) return "unknown";
  return data.claims.some((claim) => claim.state !== "assigned") ? "pending" : "clear";
}

/** A balance is zero only when both the scoped ledger and purchase setup are current. */
export function studentCreditFundingState(
  studentId: string,
  credits: LessonCreditSummary | null,
  creditsVerified: boolean,
  purchaseClaims: PurchaseClaimReadState,
): StudentCreditFundingState {
  if (
    !creditsVerified ||
    credits?.portal_student_id !== studentId ||
    !Number.isSafeInteger(credits.total_available_credits) ||
    credits.total_available_credits < 0 ||
    !Array.isArray(credits.blocks) ||
    purchaseClaims !== "clear"
  ) {
    return "unknown";
  }

  if (credits.blocks.some((block) => {
    const status = block.status.toLowerCase();
    return !Number.isSafeInteger(block.available_credits) ||
      block.available_credits < 0 ||
      block.ledger_balance === null ||
      (block.booking_held_credits ?? 0) > 0 ||
      (block.upcoming_allocated_credits ?? 0) > 0 ||
      (block.family_shared === true && block.available_credits === 0) ||
      /pending|review|reconcil|claim|hold/i.test(status);
  })) {
    return "unknown";
  }

  const usableBlock = credits.blocks.some(
    (block) => block.available_credits > 0 && block.booking.allowed,
  );
  if (usableBlock) return "available";
  if (credits.total_available_credits !== 0 ||
      credits.blocks.some((block) => block.available_credits > 0)) {
    return "unknown";
  }
  return "zero";
}

/**
 * The default credit read leaves out usage, so a block with lessons still
 * allocated to it can look empty. "No credits" is only safe to say once the
 * usage read has confirmed nothing is allocated; until then, or if it failed or
 * left a block unreported, stay silent rather than invite a second purchase.
 */
export function studentCreditRenewalGuardedState(
  state: StudentCreditFundingState,
  credits: LessonCreditSummary | null,
  usageState: CreditUsageState,
): StudentCreditFundingState {
  if (state !== "zero") return state;
  if (usageState !== "ready" || !credits) return "unknown";
  return credits.blocks.every((block) => Number.isSafeInteger(block.upcoming_allocated_credits))
    ? "zero"
    : "unknown";
}

function lessonHasPendingTutorDecision(lesson: StudentLesson): boolean {
  return (lesson.enrichment?.booking_status ?? lesson.status).toLowerCase() === "pending";
}

function lessonHasRecurringPlan(lesson: StudentLesson): boolean {
  return Boolean(lesson.enrichment?.recurrence.recurrence_plan_id);
}

function lessonActuallyOccurred(lesson: StudentLesson): boolean {
  const attendance = lesson.enrichment?.attendance_outcome.toLowerCase();
  return !lesson.unavailable &&
    lesson.enrichment !== null &&
    (attendance === "completed" || attendance === "completed_unpaid");
}

/**
 * Show a booking nudge only from authoritative lesson evidence. Pending tutor
 * decisions, recurring bookings, an unknown booking read, and a second future
 * lesson suppress the one-lesson reminder. A gap reminder requires a completed
 * attendance outcome; it never treats a cancellation or missed lesson as taught.
 */
export function studentBookingReminder(
  studentId: string,
  data: StudentLessons,
): StudentBookingReminder | null {
  if (
    !studentDashboardIdentityMatches(studentId, data) ||
    data.enrichment.state !== "ok" ||
    data.upcoming.some(lessonHasPendingTutorDecision) ||
    data.upcoming.some(lessonHasRecurringPlan)
  ) {
    return null;
  }

  if (
    data.upcoming.length === 1 &&
    data.upcoming[0]!.status === "approved" &&
    data.upcoming[0]!.enrichment?.booking_status === "approved" &&
    !data.upcoming[0]!.unavailable
  ) {
    return { kind: "one_confirmed_lesson", lesson: data.upcoming[0]! };
  }

  if (data.upcoming.length > 0) return null;
  const lastPastLesson = data.past[0];
  return lastPastLesson && lessonActuallyOccurred(lastPastLesson)
    ? { kind: "after_completed_lesson", lesson: lastPastLesson }
    : null;
}

/** Only an active ledger block with a known state is shown as available to book. */
function creditBlockAvailableForDisplay(block: LessonCreditBlock): number | null {
  if (block.status === "active") {
    return Number.isFinite(block.available_credits) && block.available_credits >= 0
      ? block.available_credits
      : null;
  }
  if (["requires_review", "expired", "depleted", "cancelled", "refunded"].includes(block.status)) {
    return 0;
  }
  return null;
}

function creditCountLabel(value: number | null | undefined): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Not reported by the booking authority";
  }
  return `${value} ${Math.abs(value) === 1 ? "credit" : "credits"}`;
}

function creditBlockStatusLabel(status: string): string {
  switch (status) {
    case "active": return "Active";
    case "requires_review": return "Under review; not available to book";
    case "expired": return "Expired";
    case "depleted": return "Depleted";
    case "cancelled": return "Cancelled";
    case "refunded": return "Refunded";
    default: return "Unknown; check with the school before relying on this block";
  }
}

function creditBlockAvailableLabel(block: LessonCreditBlock): string {
  const available = creditBlockAvailableForDisplay(block);
  if (available === null) return "Availability not confirmed by the booking authority";
  return `${available} ${available === 1 ? "lesson" : "lessons"} available to book`;
}

function creditBlockCanBeBooked(block: LessonCreditBlock): boolean {
  const available = creditBlockAvailableForDisplay(block);
  return available !== null && available > 0 && block.booking.allowed;
}

/**
 * Keep family Home actions inside the SPA router. The fallback keeps this
 * presentational view usable in isolated server-rendered tests without a router.
 */
function FamilyHomeLink({
  to,
  className,
  children,
}: {
  to: string;
  className?: string;
  children: ReactNode;
}) {
  const inRouter = useInRouterContext();
  return inRouter ? (
    <Link className={className} to={to}>
      {children}
    </Link>
  ) : (
    <a className={className} href={to}>
      {children}
    </a>
  );
}

export type CreditUsageState = "idle" | "loading" | "ready" | "error";

const USAGE_LOADING_LABEL = "Loading…";
const USAGE_ERROR_LABEL = "Could not be loaded just now";

function creditUsageLabel(value: number | null | undefined, state: CreditUsageState): string {
  if (state === "loading") return USAGE_LOADING_LABEL;
  if (state === "error") return USAGE_ERROR_LABEL;
  return creditCountLabel(value);
}

export interface StudentDashboardViewProps {
  studentId: string;
  data: StudentLessons;
  credits: LessonCreditSummary | null;
  creditsLoading: boolean;
  creditsError: string | null;
  purchaseClaimState?: PurchaseClaimReadState;
  /** Only true after the current FastAPI offerings read allows an on-site request. */
  onSiteRequestAvailable?: boolean;
  /** Where the Hub was configured to send families to buy credits; absent hides the link. */
  creditPurchaseUrl?: string | null;
  onRetryCredits?: () => void;
  /**
   * The per-block usage figures cost the booking authority a read of the
   * learner's appointments, so they are fetched only once a breakdown is opened.
   */
  creditUsageState?: CreditUsageState;
  onCreditUsageRequested?: () => void;
  changingBookingId?: string | null;
  changingLesson?: { bookingId: string; mode: ChangeMode } | null;
  onChangeLesson?: (lesson: StudentLesson) => void;
  onManageLesson?: (lesson: StudentLesson, mode: ChangeMode) => void;
  renderChangeLesson?: (
    lesson: StudentLesson,
    open: boolean,
    mode?: ChangeMode | null,
  ) => ReactNode;
  urgentAlerts?: ReactNode;
  /** One selected learner's current task, kept beside the next lesson. */
  afterNextLesson?: ReactNode;
  afterPrimaryAction?: ReactNode;
  /** Other upcoming lessons (School) shown after Coming up, above lesson history. */
  afterUpcoming?: ReactNode;
  afterCredits?: ReactNode;
}

export const HOME_CREDIT_PAGE_SIZE = HOME_CREDIT_PREVIEW_PAGE_SIZE;
const EMPTY_CREDIT_BLOCKS: LessonCreditBlock[] = [];

/**
 * The read-only dashboard model.
 *
 * The values here are already scoped to the logged-in Hub student and already
 * calculated by the Hub/FastAPI boundary. This component formats them; it does
 * not infer ownership, availability, attendance or credit balances.
 */
export function StudentDashboardView({
  studentId,
  data,
  credits,
  creditsLoading,
  creditsError,
  purchaseClaimState = "unknown",
  onSiteRequestAvailable = false,
  creditPurchaseUrl = null,
  onRetryCredits,
  creditUsageState = "ready",
  onCreditUsageRequested,
  changingBookingId = null,
  changingLesson = null,
  onChangeLesson,
  onManageLesson,
  renderChangeLesson,
  urgentAlerts,
  afterNextLesson,
  afterPrimaryAction,
  afterUpcoming,
  afterCredits,
}: StudentDashboardViewProps) {
  const studentCredits = credits?.portal_student_id === studentId ? credits : null;
  const creditBalanceVerified = studentCredits !== null && !creditsLoading && creditsError === null;
  const bookingReminder = studentBookingReminder(studentId, data);
  const creditFundingState = studentCreditRenewalGuardedState(
    studentCreditFundingState(
      studentId,
      studentCredits,
      creditBalanceVerified,
      purchaseClaimState,
    ),
    studentCredits,
    creditUsageState,
  );
  const creditBlocks = useMemo(
    () => studentCredits
      ? [...studentCredits.blocks].sort(
          (a, b) => Number((creditBlockAvailableForDisplay(b) ?? 0) > 0) - Number((creditBlockAvailableForDisplay(a) ?? 0) > 0),
        )
      : EMPTY_CREDIT_BLOCKS,
    [studentCredits],
  );
  const creditPages = usePagination(creditBlocks, HOME_CREDIT_PAGE_SIZE, studentId);
  // The credits disclosure starts closed, but an error or a lesson block the
  // family can book right now is something to act on, so it opens for those. A
  // person's own open or close is kept until that situation changes.
  const creditNeedsAttention = creditsError !== null
    || creditBlocks.some(creditBlockCanBeBooked);
  const [creditChoice, setCreditChoice] = useState<{ attention: boolean; open: boolean } | null>(null);
  const creditOpen = creditChoice !== null && creditChoice.attention === creditNeedsAttention
    ? creditChoice.open
    : creditNeedsAttention;
  if (!studentDashboardIdentityMatches(studentId, data)) {
    return (
      <div className="error">
        We could not match these lessons to the signed-in student. Please refresh and try again.
      </div>
    );
  }

  const total = studentCredits?.total_available_credits ?? 0;
  const recurringPlans = studentRecurringPlanSummaries(data.upcoming);
  const nextLesson = data.upcoming[0] ?? null;
  const comingUp = data.upcoming.slice(1, 4);
  const remainingUpcoming = data.upcoming.slice(4, HOME_UPCOMING_ROWS);
  const hasOlderUpcoming = data.upcoming.length > HOME_UPCOMING_ROWS;
  const recentPast = data.past.slice(0, HOME_PAST_ROWS);
  const hasOlderPast = data.past.length > recentPast.length;
  const hasLaterCalendarExport = [...comingUp, ...data.upcoming.slice(4)].some(
    studentLessonCanExportCalendar,
  );
  const manageLesson = (lesson: StudentLesson, mode: ChangeMode) => {
    if (onManageLesson) onManageLesson(lesson, mode);
    else onChangeLesson?.(lesson);
  };
  const openFor = (lesson: StudentLesson) =>
    changingLesson?.bookingId === lesson.bookingId || changingBookingId === lesson.bookingId;
  const modeFor = (lesson: StudentLesson): ChangeMode | null =>
    changingLesson?.bookingId === lesson.bookingId ? changingLesson.mode : null;

  return (
    <>
      <div className="student-home-columns">
        <div className="student-home-column-main">
          <section className="student-home-next" aria-labelledby="next-lesson-heading">
            <h2 id="next-lesson-heading">Next lesson</h2>
            {nextLesson ? (
              <>
                <NextLessonHero
                  lesson={nextLesson}
                  credits={studentCredits}
                  studentId={studentId}
                  onManage={
                    onManageLesson || onChangeLesson
                      ? (mode) => manageLesson(nextLesson, mode)
                      : undefined
                  }
                  open={openFor(nextLesson)}
                  openMode={modeFor(nextLesson)}
                  policyState={data.enrichment.state}
                />
                {renderChangeLesson?.(
                  nextLesson,
                  openFor(nextLesson),
                  modeFor(nextLesson),
                )}
              </>
            ) : (
              <div className="panel student-home-next-panel student-home-next-empty">
                <div>
                  <strong>No upcoming lesson</strong>
                  <p className="small muted">There is nothing booked at the moment.</p>
                </div>
              </div>
            )}
          </section>

          {urgentAlerts}

          {afterNextLesson}

          {bookingReminder && (
            <div className="notice small" role="status" aria-label="Book your next lesson">
              {bookingReminder.kind === "one_confirmed_lesson" ? (
                <>
                  <strong>One confirmed lesson remains booked.</strong>
                  <p>
                    Your last currently booked lesson is on {formatDate(bookingReminder.lesson.startsAt)} at{" "}
                    {formatTime(bookingReminder.lesson.startsAt)}.
                  </p>
                </>
              ) : (
                <>
                  <strong>Your last lesson was marked as taught.</strong>
                  <p>Book your next lesson whenever you are ready.</p>
                </>
              )}
              <p>Nothing is booked or purchased automatically.</p>
            </div>
          )}

          {data.enrichment.state === "unavailable" && (
            <div className="notice small">
              Your lessons are listed below, but payment details, booking and changes are not
              available at the moment.
              {data.enrichment.message ? ` ${data.enrichment.message}` : ""}
            </div>
          )}

          <nav className="student-home-primary-action" aria-label="Lesson actions">
            <FamilyHomeLink className="button action-primary" to={`/book?student=${encodeURIComponent(studentId)}`}>
              {bookingReminder ? "Book your next lesson" : nextLesson ? "Book or request another lesson" : "Book or request a lesson"}
            </FamilyHomeLink>
          </nav>

          {creditFundingState === "zero" && (
            <section className="notice small student-home-credit-funding" aria-label="Lesson payment choices">
              <strong>No lesson credits are currently available to book.</strong>
              <p>
                Website purchases may need time to be matched to your learner before they can be used.
                Check your credits before purchasing again.
              </p>
              {creditPurchaseUrl && (
                <p>
                  <a href={creditPurchaseUrl} target="_blank" rel="noreferrer">
                    Buy lesson credits on the WMF website
                  </a>
                </p>
              )}
              {onSiteRequestAvailable && (
                <>
                  <p>
                    <FamilyHomeLink className="button action-secondary" to={`/book?student=${encodeURIComponent(studentId)}`}>
                      Request a lesson and pay your tutor on the day
                    </FamilyHomeLink>
                  </p>
                  <p className="small muted">
                    A pay-on-the-day request is not a confirmed booking until your tutor approves it.
                    The Hub does not take payment. The booking page checks this option again for the selected time.
                  </p>
                </>
              )}
            </section>
          )}

          {creditBalanceVerified && studentCredits.total_available_credits === 0 &&
            purchaseClaimState === "pending" && (
              <div className="notice small" role="status">
                A recent website purchase is still being matched to a learner. Check your purchase setup or credits before buying again.
              </div>
            )}
          {creditBalanceVerified && studentCredits.total_available_credits === 0 &&
            purchaseClaimState === "unknown" && (
              <div className="notice small" role="status">
                We could not confirm whether a recent purchase is still being set up. Check your purchase setup or retry your credits before buying more.
              </div>
            )}

          {afterPrimaryAction}

          {comingUp.length > 0 && (
            <details className="ui-disclosure student-home-upcoming-details">
              <summary>Coming up</summary>
              <div className="ui-disclosure-body">
                <section aria-labelledby="coming-up-heading">
                  <h2 id="coming-up-heading">Coming up</h2>
                  {hasLaterCalendarExport && <CalendarCopyNote />}
                  <div className="panel">
                    <UpcomingLessonList
                      lessons={comingUp}
                      credits={studentCredits}
                      studentId={studentId}
                      changingBookingId={changingBookingId}
                      changingLesson={changingLesson}
                      onChangeLesson={onChangeLesson}
                      onManageLesson={onManageLesson || onChangeLesson ? manageLesson : undefined}
                      renderChangeLesson={renderChangeLesson}
                      policyState={data.enrichment.state}
                    />
                  </div>

                  {remainingUpcoming.length > 0 && (
                    <details className="ui-disclosure student-home-more-lessons">
                      <summary>
                        Show {remainingUpcoming.length} more upcoming{" "}
                        {remainingUpcoming.length === 1 ? "lesson" : "lessons"}
                      </summary>
                      <div className="ui-disclosure-body panel">
                        <UpcomingLessonList
                          lessons={remainingUpcoming}
                          credits={studentCredits}
                          studentId={studentId}
                          changingBookingId={changingBookingId}
                          changingLesson={changingLesson}
                          onChangeLesson={onChangeLesson}
                          onManageLesson={onManageLesson || onChangeLesson ? manageLesson : undefined}
                          renderChangeLesson={renderChangeLesson}
                          policyState={data.enrichment.state}
                        />
                      </div>
                    </details>
                  )}
                  {hasOlderUpcoming && (
                    <div className="student-home-section-link">
                      <FamilyHomeLink to="/lessons">View all lessons</FamilyHomeLink>
                    </div>
                  )}
                </section>
              </div>
            </details>
          )}

          {afterUpcoming}

          {recurringPlans.length > 0 && (
            <details className="ui-disclosure student-home-recurring-details">
              <summary>Recurring lessons</summary>
              <div className="ui-disclosure-body">
                <section aria-labelledby="recurring-lessons-heading">
                  <h2 id="recurring-lessons-heading">Recurring lessons</h2>
                  <div className="panel">
                    {recurringPlans.map((plan) => (
                      <div className="slot" key={plan.planId}>
                        <div style={{ flex: 1 }}>
                          <strong>{studentRecurringPlanLabel(plan.cadence)}</strong>
                          <div className="small muted">
                            {plan.serviceName ?? "Lesson"}
                            {plan.tutorName && ` · ${plan.tutorName}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </details>
          )}
        </div>

        <aside className="student-home-column-side">
          <details
            className="ui-disclosure student-home-credit-details"
            open={creditOpen}
            onToggle={(event) => {
              const open = event.currentTarget.open;
              if (open !== creditOpen) setCreditChoice({ attention: creditNeedsAttention, open });
            }}
          >
            <summary id="lesson-credit-heading">
              Lesson credits{studentCredits ? ` · ${total} available to book` : ""}
            </summary>
            <section aria-labelledby="lesson-credit-heading" className="ui-disclosure-body">
              {creditsLoading && !studentCredits && <p className="empty">Loading your credits…</p>}
              {creditsError && (
                <div className="error">
                  We could not load your credit balance right now. Your lessons are still shown above.
                  {onRetryCredits && <> <button type="button" className="quiet small" onClick={onRetryCredits}>Retry credits</button></>}
                </div>
              )}
              {studentCredits && (
                <div className="panel student-home-credits-panel">
                  {studentCredits.blocks.length === 0 ? (
                    <div className="notice">
                      <strong>No lesson blocks are showing yet</strong>
                      <div className="small muted">{recentAmeliaPurchaseMessage}</div>
                    </div>
                  ) : (
                    <>
                      <strong className="student-home-credits-total">
                        {total} {total === 1 ? "lesson" : "lessons"} available to book
                      </strong>
                      {total === 0 && (
                        <p className="small muted student-home-credits-empty">
                          There are no available credits on the lesson blocks shown below.
                        </p>
                      )}
                      <div className="student-home-credit-list">
                        {creditPages.items.map((block) => (
                          <div className="student-home-credit-row" key={block.lesson_block_id}>
                            <div className="student-home-credit-copy">
                              <strong className="student-home-credit-name">{block.service_name}</strong>
                              <span className="small muted">
                                {block.source === "paid_cancellation" ? "Paid cancellation credit · " : ""}
                                {creditBlockAvailableLabel(block)}
                                {block.expires_at ? ` · expires ${formatDate(block.expires_at)}` : ""}
                              </span>
                              <details
                                className="ui-disclosure student-home-credit-breakdown"
                                onToggle={(event) => { if (event.currentTarget.open) onCreditUsageRequested?.(); }}
                              >
                                <summary>Balance breakdown</summary>
                                <div className="ui-disclosure-body">
                                  <dl className="small">
                                    <div><dt>Available to book</dt><dd>{creditBlockAvailableLabel(block)}</dd></div>
                                    <div><dt>Ledger balance</dt><dd>{creditCountLabel(block.ledger_balance)}</dd></div>
                                    <div><dt>Temporary booking holds</dt><dd>{creditCountLabel(block.booking_held_credits)}</dd></div>
                                    <div><dt>Consumed from this block</dt><dd>{creditUsageLabel(block.consumed_credits, creditUsageState)}</dd></div>
                                    <div><dt>Completed lesson use</dt><dd>{creditUsageLabel(block.completed_used_credits, creditUsageState)}</dd></div>
                                    <div><dt>Upcoming allocated use</dt><dd>{creditUsageLabel(block.upcoming_allocated_credits, creditUsageState)}</dd></div>
                                    <div><dt>Ledger state</dt><dd>{creditBlockStatusLabel(block.status)}</dd></div>
                                  </dl>
                                  <p className="small muted">Only the available-to-book figure is treated as spendable. Holds stay separate. Upcoming allocations and completed lesson use appear separately only when the booking authority supplies them; no exhaustion estimate is inferred from missing values.</p>
                                  {block.family_shared && <p className="small muted">This block is shared by the family and may appear in more than one learner’s view. Do not add balances across children.</p>}
                                </div>
                              </details>
                              <span className="small muted">Block reference {creditBlockReference(block.lesson_block_id)}{block.purchased_at ? ` · purchased ${formatDate(block.purchased_at)}` : ""}</span>
                              <FamilyHomeLink to={creditDetailsLink(studentId, block.lesson_block_id)}>View details</FamilyHomeLink>
                            </div>
                            {creditBlockCanBeBooked(block) && (
                              <FamilyHomeLink className="button action-primary student-home-credit-book" to={creditBookingLink(studentId, block.lesson_block_id)}>
                                Book lesson
                              </FamilyHomeLink>
                            )}
                          </div>
                        ))}
                      </div>
                      <Pagination
                        ariaLabel="Lesson credits pages"
                        previousAriaLabel="Previous lesson credits page"
                        nextAriaLabel="Next lesson credits page"
                        page={creditPages.page}
                        pages={creditPages.pages}
                        onPrevious={creditPages.previous}
                        onNext={creditPages.next}
                      />
                    </>
                  )}
                  <div className="student-home-section-link">
                    <FamilyHomeLink to={creditDetailsLink(studentId)}>View credit details</FamilyHomeLink>
                  </div>
                </div>
              )}
            </section>
          </details>
          {afterCredits}
        </aside>
      </div>

      {/* Below the columns, so Home's lesson and practice notes always follow it
          directly, even when the credits column is taller or the columns stack. */}
      {data.past.length > 0 && (
        <details className="ui-disclosure student-home-lesson-history">
          <summary>Recent lesson history</summary>
          <div className="panel">
            {recentPast.map((lesson) => (
              <PastLesson key={lesson.bookingId} lesson={lesson} />
            ))}
            {hasOlderPast && (
              <div className="student-home-section-link">
                <FamilyHomeLink to="/lessons?view=past">View all lessons</FamilyHomeLink>
              </div>
            )}
          </div>
        </details>
      )}
    </>
  );
}

/** Backward-compatible entry point for the Home dashboard. */
export function StudentDashboard({
  studentId,
  afterPrimaryAction,
}: {
  studentId: string;
  afterPrimaryAction?: ReactNode;
}) {
  return <StudentHome studentId={studentId} afterPrimaryAction={afterPrimaryAction} />;
}

/** Home is composed around the next lesson, with urgent offers immediately after it. */
export function StudentHome({
  studentId,
  afterPrimaryAction,
}: {
  studentId: string;
  afterPrimaryAction?: ReactNode;
}) {
  const lessons = useAsync(() => booking.lessons(studentId), [studentId]);
  const credits = useAsync(() => booking.credits(studentId), [studentId]);
  const [usageRequestedFor, setUsageRequestedFor] = useState<string | null>(null);
  const usageRequested = usageRequestedFor === studentId;
  const usage = useAsync(
    () => (usageRequested ? booking.credits(studentId, { includeUsage: true }) : Promise.resolve(null)),
    [studentId, usageRequested],
  );
  const creditUsageState: CreditUsageState = !usageRequested
    ? "idle"
    : usage.loading ? "loading" : usage.error ? "error" : "ready";
  const creditsWithUsage = useMemo(() => {
    if (!credits.data || !usage.data || creditUsageState !== "ready") return credits.data;
    const byId = new Map(usage.data.blocks.map((block) => [block.lesson_block_id, block]));
    return {
      ...credits.data,
      blocks: credits.data.blocks.map((block) => {
        const detail = byId.get(block.lesson_block_id);
        return detail
          ? {
              ...block,
              consumed_credits: detail.consumed_credits,
              completed_used_credits: detail.completed_used_credits,
              upcoming_allocated_credits: detail.upcoming_allocated_credits,
            }
          : block;
      }),
    };
  }, [credits.data, usage.data, creditUsageState]);
  const purchaseSetup = useAsync(() => get<FamilySetupData>("/family-setup"), [studentId]);
  const configuredPurchaseUrl = useOptionalAuth()?.me?.booking?.purchaseUrl;
  // Only an http(s) address the Hub was configured with; nothing is guessed.
  const creditPurchaseUrl = typeof configuredPurchaseUrl === "string" && /^https?:\/\//i.test(configuredPurchaseUrl)
    ? configuredPurchaseUrl
    : null;
  const purchaseClaimState = familyPurchaseClaimReadState(
    purchaseSetup.data,
    purchaseSetup.loading,
    purchaseSetup.error,
  );
  const defaultReadShowsZero =
    studentCreditFundingState(
      studentId,
      credits.loading ? null : credits.data,
      !credits.loading && credits.error === null,
      purchaseClaimState,
    ) === "zero";
  useEffect(() => {
    if (defaultReadShowsZero) setUsageRequestedFor(studentId);
  }, [defaultReadShowsZero, studentId]);
  const creditBalanceVerified = credits.data !== null && !credits.loading && credits.error === null;
  const onSiteOfferingsNeeded = studentCreditFundingState(
    studentId,
    credits.error === null ? credits.data : null,
    creditBalanceVerified,
    purchaseClaimState,
  ) === "zero";
  const onSiteOfferings = useAsync(
    () => onSiteOfferingsNeeded ? booking.offerings(studentId) : Promise.resolve(null),
    [studentId, onSiteOfferingsNeeded],
  );
  const onSiteRequestAvailable = onSiteOfferingsNeeded &&
    !onSiteOfferings.loading &&
    onSiteOfferings.error === null &&
    studentHasEligibleOnSiteRequest(studentId, onSiteOfferings.data);
  const [changing, setChanging] = useState<{
    bookingId: string;
    mode: ChangeMode;
  } | null>(null);

  const loadState = studentDashboardLoadState({
    lessonsLoading: lessons.loading,
    lessonsData: lessons.data,
    lessonsError: lessons.error,
    creditsLoading: credits.loading,
  });

  const reloadAll = () => {
    lessons.reload();
    credits.reload();
    onSiteOfferings.reload();
  };

  if (loadState === "loading") return <p className="empty">Loading your lessons and credits…</p>;
  if (loadState === "error") {
    return (
      <div className="error" role="alert">
        We could not load your lesson dashboard right now. Please refresh and try again.
        <button type="button" className="quiet small" onClick={() => { lessons.reload(); credits.reload(); }}>Retry dashboard</button>
      </div>
    );
  }
  if (!lessons.data) return null;

  return (
    <StudentDashboardView
      studentId={studentId}
      data={lessons.data}
      credits={credits.loading || credits.error ? null : creditsWithUsage}
      creditUsageState={creditUsageState}
      onCreditUsageRequested={() => setUsageRequestedFor(studentId)}
      creditsLoading={credits.loading}
      creditsError={credits.error}
      purchaseClaimState={purchaseClaimState}
      onSiteRequestAvailable={onSiteRequestAvailable}
      creditPurchaseUrl={creditPurchaseUrl}
      onRetryCredits={credits.reload}
      changingLesson={changing}
      onManageLesson={(lesson, mode) =>
        setChanging((current) =>
          current?.bookingId === lesson.bookingId && current.mode === mode
            ? null
            : { bookingId: lesson.bookingId, mode },
        )
      }
      renderChangeLesson={(lesson, open, mode) =>
        open && mode ? (
          <ChangeLesson
            key={`${lesson.bookingId}:${mode}`}
            studentId={studentId}
            lesson={lesson}
            onDone={reloadAll}
            onClose={() => setChanging(null)}
            initialMode={mode}
          />
        ) : null
      }
      urgentAlerts={
        <FamilyWaitlistOffers key={studentId} studentId={studentId} onAccepted={reloadAll} />
      }
      afterPrimaryAction={afterPrimaryAction}
      afterUpcoming={<CalendarFeedSubscription studentId={studentId} />}
    />
  );
}

/** The booking actions live on their own route so Home stays a dashboard. */
export function StudentBooking({ studentId, creditId = null, creditStudentId = null, creditState, onClearCredit, onCreditBooked }: {
  studentId: string;
  creditId?: string | null;
  creditStudentId?: string | null;
  /** Reuse the family attendee check, including its loading/error and refresh state. */
  creditState?: Async<LessonCreditSummary | null>;
  onClearCredit?: () => void;
  onCreditBooked?: () => void;
}) {
  const offerings = useAsync(() => booking.offerings(studentId), [studentId]);
  const fetchedCredits = useAsync(() => creditId && !creditState ? booking.credits(studentId) : Promise.resolve(null),
    [studentId, creditId, Boolean(creditState)]);
  const credits = creditState ?? fetchedCredits;
  const [waitlistRefresh, setWaitlistRefresh] = useState(0);
  const currentOfferings = offerings.data?.portal_student_id === studentId ? offerings.data.offerings : null;
  const selectedCredit = creditId && creditStudentId === studentId && currentOfferings
    ? resolveCreditBooking(studentId, creditId, credits.data, currentOfferings) : null;
  // Keep an already-open picker mounted after spending its last credit or a
  // failed refresh, so its confirmation/retry evidence survives. Retained data
  // is presentation only: revoke both funding paths until validation succeeds.
  const contextKey = JSON.stringify([studentId, creditStudentId, creditId]);
  const validated = useRef<{ key: string; value: NonNullable<typeof selectedCredit> } | null>(null);
  useEffect(() => {
    if (selectedCredit) validated.current = { key: contextKey, value: selectedCredit };
  }, [contextKey, selectedCredit]);
  const checkingCredit = Boolean(creditId && (credits.loading || offerings.loading));
  // Keep a validated picker instance mounted through the parent balance read,
  // but never treat its retained data as fresh eligibility. This lets a
  // confirmed receipt survive while both reads are pending or fail without
  // leaving another credit-funded booking enabled against a stale balance.
  const invalidCredit = Boolean(creditId && (checkingCredit || !selectedCredit || credits.error || offerings.error));
  const previous = validated.current?.key === contextKey ? validated.current.value : null;
  const retained = invalidCredit && previous ? {
    block: { ...previous.block, available_credits: 0, booking: { allowed: false, reasons: [] } },
    offering: { ...previous.offering, available_credits: 0, book_with_credit: { allowed: false, reasons: [] }, request_onsite: { allowed: false, reasons: [] } },
  } : null;
  const creditContext = invalidCredit ? retained : selectedCredit;
  const reload = () => { offerings.reload(); if (creditId) credits.reload(); onCreditBooked?.(); };

  return (
    <>
      {offerings.loading && !offerings.data && (
        <p className="empty">Loading your booking options…</p>
      )}
      {offerings.error && <div className="error">{offerings.error}</div>}
      {creditId && invalidCredit && (
        <div className="notice" role="status">
          {checkingCredit
            ? "Checking the selected lesson credit. Another booking with it is unavailable until the current balance and lesson eligibility are confirmed."
            : credits.error || offerings.error
              ? "The current balance or lesson eligibility could not be checked. Another booking with this credit is unavailable until you check again."
              : "This credit block is no longer available for this student and lesson. Choose another lesson or return Home to check your credits."}
          {!checkingCredit && onClearCredit && <button type="button" className="action-secondary" onClick={onClearCredit}>Choose another lesson</button>}
        </div>
      )}
      {(currentOfferings || retained) && (!invalidCredit || retained) && (
        <BookLesson
          studentId={studentId}
          offerings={retained ? [retained.offering] : currentOfferings!}
          creditBlock={creditContext?.block}
          creditUnconfirmed={Boolean(retained)}
          onClearCredit={onClearCredit}
          onBooked={reload}
          onWaitlistChanged={() => setWaitlistRefresh((value) => value + 1)}
        />
      )}
      <FamilyWaitlistRequests studentId={studentId} refreshNonce={waitlistRefresh} />
    </>
  );
}

function NextLessonHero({
  lesson,
  credits,
  studentId,
  onManage,
  open,
  openMode,
  policyState,
}: {
  lesson: StudentLesson;
  credits: LessonCreditSummary | null;
  studentId: string;
  onManage: ((mode: ChangeMode) => void) | undefined;
  open: boolean;
  openMode: ChangeMode | null;
  policyState: StudentLessons["enrichment"]["state"];
}) {
  const duration = lessonDuration(lesson);
  const location = lessonLocation(lesson);
  const canExportCalendar = studentLessonCanExportCalendar(lesson);

  return (
    <div className="panel student-home-next-panel">
      <div className="student-home-next-grid">
        <div>
          <div className="student-home-next-date">{formatDay(lesson.startsAt)}</div>
          <div className="student-home-next-time">{formatTime(lesson.startsAt)}</div>
        </div>
        <div className="student-home-next-meta">
          <strong>{lesson.serviceName ?? "Lesson"}</strong>
          <div>{lesson.tutorName ? `with ${lesson.tutorName}` : "Tutor not shown"}</div>
          {duration && <div>{duration}</div>}
          {location && <div>{location}</div>}
          <div className="student-home-payment">
            <LessonStatusBadges lesson={lesson} credits={credits} studentId={studentId} showPaymentFallback />
          </div>
        </div>
      </div>

      <div className="student-home-next-actions">
        <LessonChangeActions
          lesson={lesson}
          onManage={onManage}
          open={open}
          openMode={openMode}
          policyState={policyState}
          variant="hero"
        />
        {canExportCalendar && (
          <AddToCalendarButton
            lesson={{
              id: lesson.bookingId,
              startsAt: lesson.startsAt,
              endsAt: lesson.endsAt,
              title: lesson.serviceName,
              tutorName: lesson.tutorName,
              location,
            }}
          />
        )}
      </div>
      {canExportCalendar && <CalendarCopyNote className="student-home-calendar-note" />}
    </div>
  );
}

function CalendarCopyNote({ className = "calendar-copy-note" }: { className?: string }) {
  return (
    <p className={`small muted ${className}`}>
      Add to calendar creates a one-off copy. Later changes or cancellations in the Hub will not
      update that copy automatically.
    </p>
  );
}

function lessonDuration(lesson: StudentLesson): string | null {
  const seconds = (Date.parse(lesson.endsAt) - Date.parse(lesson.startsAt)) / 1000;
  return Number.isFinite(seconds) && seconds > 0 ? formatDuration(seconds) : null;
}

/**
 * Location is intentionally presentation-only here. The current family lesson
 * contract does not expose one, so do not guess from service, tutor or school.
 * If the read model gains a display label, the Home can use it without changing
 * the component shape.
 */
function lessonLocation(lesson: StudentLesson): string | null {
  const candidate = lesson as StudentLesson & { locationName?: unknown; location?: unknown };
  if (typeof candidate.locationName === "string" && candidate.locationName.trim()) {
    return candidate.locationName.trim();
  }
  if (typeof candidate.location === "string" && candidate.location.trim()) {
    return candidate.location.trim();
  }
  return null;
}

function UpcomingLessonList({
  lessons,
  credits,
  studentId,
  changingBookingId,
  changingLesson,
  onChangeLesson,
  onManageLesson,
  renderChangeLesson,
  policyState,
}: {
  lessons: readonly StudentLesson[];
  credits: LessonCreditSummary | null;
  studentId: string;
  changingBookingId: string | null;
  changingLesson: { bookingId: string; mode: ChangeMode } | null;
  onChangeLesson: ((lesson: StudentLesson) => void) | undefined;
  onManageLesson: ((lesson: StudentLesson, mode: ChangeMode) => void) | undefined;
  renderChangeLesson:
    | ((lesson: StudentLesson, open: boolean, mode?: ChangeMode | null) => ReactNode)
    | undefined;
  policyState: StudentLessons["enrichment"]["state"];
}) {
  return (
    <>
      {lessons.map((lesson) => {
        const open =
          changingLesson?.bookingId === lesson.bookingId || changingBookingId === lesson.bookingId;
        const openMode = changingLesson?.bookingId === lesson.bookingId ? changingLesson.mode : null;
        return (
          <div key={lesson.bookingId}>
            <Upcoming
              lesson={lesson}
              credits={credits}
              studentId={studentId}
              onManage={
                onManageLesson || onChangeLesson
                  ? (mode) =>
                      onManageLesson ? onManageLesson(lesson, mode) : onChangeLesson?.(lesson)
                  : undefined
              }
              open={open}
              openMode={openMode}
              policyState={policyState}
            />
            {renderChangeLesson?.(lesson, open, openMode)}
          </div>
        );
      })}
    </>
  );
}

function Upcoming({
  lesson,
  credits,
  studentId,
  onManage,
  open,
  openMode,
  policyState,
}: {
  lesson: StudentLesson;
  credits: LessonCreditSummary | null;
  studentId: string;
  onManage: ((mode: ChangeMode) => void) | undefined;
  open: boolean;
  openMode: ChangeMode | null;
  policyState: StudentLessons["enrichment"]["state"];
}) {
  const canExportCalendar = studentLessonCanExportCalendar(lesson);
  const location = lessonLocation(lesson);

  return (
    <div className="slot student-home-upcoming-row">
      <time>{formatTime(lesson.startsAt)}</time>
      <div style={{ flex: 1 }}>
        <div>{formatDate(lesson.startsAt)}</div>
        <div className="small muted">
          {lesson.serviceName ?? "Lesson"}
          {lesson.tutorName && ` · ${lesson.tutorName}`}
        </div>
      </div>
      <div className="lesson-actions">
        <LessonStatusBadges lesson={lesson} credits={credits} studentId={studentId} />
        <LessonChangeActions
          lesson={lesson}
          onManage={onManage}
          open={open}
          openMode={openMode}
          policyState={policyState}
          variant="compact"
        />
        {canExportCalendar && (
          <AddToCalendarButton
            lesson={{
              id: lesson.bookingId,
              startsAt: lesson.startsAt,
              endsAt: lesson.endsAt,
              title: lesson.serviceName,
              tutorName: lesson.tutorName,
              location,
            }}
          />
        )}
      </div>
    </div>
  );
}

function LessonStatusBadges({
  lesson,
  credits,
  studentId,
  showPaymentFallback = false,
}: {
  lesson: StudentLesson;
  credits: LessonCreditSummary | null;
  studentId: string;
  showPaymentFallback?: boolean;
}) {
  const payment = studentPaymentBadge(
    lesson.enrichment?.payment,
    studentLessonHasAvailableCredit(lesson, credits, studentId),
  );
  const status = lesson.enrichment?.booking_status ?? lesson.status;

  return (
    <>
      {payment ? (
        <span className={payment.tone === "warning" ? "tag warn" : "tag"}>{payment.label}</span>
      ) : (
        showPaymentFallback && <span className="small muted">Payment details unavailable</span>
      )}
      <span className="tag">{studentBookingStatusLabel(status)}</span>
    </>
  );
}

export function LessonChangeActions({
  lesson,
  onManage,
  open,
  openMode,
  policyState,
  variant,
}: {
  lesson: StudentLesson;
  onManage: ((mode: ChangeMode) => void) | undefined;
  open: boolean;
  openMode: ChangeMode | null;
  policyState: StudentLessons["enrichment"]["state"];
  variant: "hero" | "compact";
}) {
  const actions = lesson.enrichment?.actions;
  const isSchool = lesson.domain === "school";
  const readOnly = lesson.canManageBooking === false;
  const inactiveStatuses = ["cancelled", "canceled", "inactive", "rejected", "completed"];
  const inactive = [lesson.status, lesson.enrichment?.booking_status].some(
    (status) => status !== undefined && inactiveStatuses.includes(status.toLowerCase()),
  );

  if (isSchool) {
    return (
      <span className="small muted">
        School lessons are managed by the school.
      </span>
    );
  }

  if (readOnly) {
    return <span className="small muted">This lesson cannot be changed from the Hub.</span>;
  }

  if (lesson.unavailable || policyState === "unavailable") {
    return <span className="small muted">Lesson changes are temporarily unavailable.</span>;
  }

  if (!actions) return <span className="small muted">Checking lesson options…</span>;

  if (inactive) return <span className="small muted">This lesson is no longer active.</span>;

  const buttonClass = variant === "hero" ? "action-secondary" : "quiet small";
  const moveLabel = open && openMode === "move" ? "Close move options" : "Move lesson";
  const cancelLabel = open && openMode === "cancel" ? "Close cancellation" : "Cancel lesson";
  const changeable = actions.cancel.allowed || actions.reschedule.allowed;
  if (onManage && changeable) {
    return (
      <>
        {actions.reschedule.allowed && (
          <button
            className={buttonClass}
            onClick={() => onManage("move")}
            type="button"
          >
            {moveLabel}
          </button>
        )}
        {actions.cancel.allowed && (
          <button
            className="action-danger"
            onClick={() => onManage("cancel")}
            type="button"
          >
            {cancelLabel}
          </button>
        )}
        {!actions.reschedule.allowed && <Reasons decision={actions.reschedule} />}
        {!actions.cancel.allowed && <Reasons decision={actions.cancel} />}
      </>
    );
  }

  if (!changeable) {
    return (
      <>
        <Reasons decision={actions.reschedule} />
        <Reasons decision={actions.cancel} />
      </>
    );
  }

  return null;
}

function PastLesson({ lesson }: { lesson: StudentLesson }) {
  return (
    <div className="slot">
      <time>{formatTime(lesson.startsAt)}</time>
      <div style={{ flex: 1 }}>
        <div>{formatDate(lesson.startsAt)}</div>
        <div className="small muted">
          {lesson.serviceName ?? "Lesson"}
          {lesson.tutorName && ` · ${lesson.tutorName}`}
          {lesson.enrichment && ` · ${describePayment(lesson.enrichment.payment)}`}
        </div>
      </div>
      <span className="tag">
        {lesson.enrichment
          ? outcomeLabel(lesson.enrichment.attendance_outcome, lesson.enrichment.booking_status)
          : studentBookingStatusLabel(lesson.status)}
      </span>
    </div>
  );
}
